import { Graphics, Text } from 'pixi.js';
import { BaseScene } from './BaseScene';
import { Button } from '../ui/Button';
import { GameScene } from './GameScene';
import type { Difficulty, SceneContext } from '../types/game.types';
import { getDifficulty, setDifficulty } from '../state/gameSettings';
import { logger } from '../utils/logger';
import { clamp, easeOutCubic, lerp } from '../utils/animation';
import { FONT_DISPLAY, FONT_UI, PALETTE } from '../ui/theme';

export class SplashScene extends BaseScene {
  private eyebrow: Text | null = null;
  private title: Text | null = null;
  private subtitle: Text | null = null;
  private detail: Text | null = null;
  private footer: Text | null = null;
  private kiwiDisplay: Text | null = null;
  private coconutDisplay: Text | null = null;
  private versusText: Text | null = null;
  private playButton: Button | null = null;
  private difficultyLabel: Text | null = null;
  private diffButtons: Button[] = [];
  private selectedDifficulty: Difficulty = getDifficulty();
  private background: Graphics | null = null;
  private visualBackdrop: Graphics | null = null;
  private horizon: Graphics | null = null;
  private introStartedAt: number = 0;
  private elapsed: number = 0;

  constructor(context: SceneContext) {
    super(context);
  }

  async load(): Promise<void> {
    await Promise.resolve();
  }

  enter(): void {
    logger.info('Entering SplashScene');
    this.elapsed = 0;
    this.introStartedAt = performance.now();

    this.background = new Graphics();
    this.addChild(this.background);

    this.visualBackdrop = new Graphics();
    this.addChild(this.visualBackdrop);

    this.horizon = new Graphics();
    this.addChild(this.horizon);

    this.eyebrow = new Text('ORCHARD DUEL', {
      fontFamily: FONT_UI,
      fontSize: 13,
      fill: PALETTE.mist,
      fontWeight: 'bold',
      letterSpacing: 3.2,
    });
    this.addChild(this.eyebrow);

    this.title = new Text('Kiwis\nvs Coconuts', {
      fontFamily: FONT_DISPLAY,
      fontSize: 92,
      fill: PALETTE.ink,
      fontWeight: 'bold',
      lineHeight: 88,
      letterSpacing: 0.8
    });
    this.title.anchor.set(0, 0);
    this.addChild(this.title);

    this.subtitle = new Text('A polished 7x7 fruit skirmish with clean turns and fast rounds.', {
      fontFamily: FONT_UI,
      fontSize: 24,
      fill: PALETTE.mist,
      lineHeight: 32,
      wordWrap: true,
      wordWrapWidth: 420,
    });
    this.addChild(this.subtitle);

    this.detail = new Text(
      'You open as the kiwis. Take the center, pressure the lanes, and finish the round before the orchard turns.',
      {
        fontFamily: FONT_UI,
        fontSize: 16,
        fill: PALETTE.mist,
        lineHeight: 24,
        wordWrap: true,
        wordWrapWidth: 440,
      },
    );
    this.addChild(this.detail);

    this.footer = new Text('Single-player match against the orchard AI.', {
      fontFamily: FONT_UI,
      fontSize: 14,
      fill: PALETTE.mist,
      letterSpacing: 0.6,
    });
    this.addChild(this.footer);

    this.kiwiDisplay = new Text('🥝', {
      fontFamily: FONT_UI,
      fontSize: 172,
    });
    this.kiwiDisplay.anchor.set(0.5);
    this.addChild(this.kiwiDisplay);

    this.coconutDisplay = new Text('🥥', {
      fontFamily: FONT_UI,
      fontSize: 168,
    });
    this.coconutDisplay.anchor.set(0.5);
    this.addChild(this.coconutDisplay);

    this.versusText = new Text('VS', {
      fontFamily: FONT_UI,
      fontSize: 24,
      fill: PALETTE.accent,
      fontWeight: 'bold',
      letterSpacing: 4,
    });
    this.versusText.anchor.set(0.5);
    this.addChild(this.versusText);

    this.difficultyLabel = new Text('Difficulty', {
      fontFamily: FONT_UI,
      fontSize: 13,
      fill: PALETTE.mist,
      fontWeight: 'bold',
      letterSpacing: 2.4,
    });
    this.addChild(this.difficultyLabel);

    const difficulties: Difficulty[] = ['easy', 'medium', 'hard'];
    const diffLabels = ['Easy', 'Medium', 'Hard'];
    this.diffButtons = [];
    for (let i = 0; i < difficulties.length; i++) {
      const d = difficulties[i];
      const btn = new Button({
        text: diffLabels[i],
        width: 88,
        height: 40,
        backgroundColor: PALETTE.surface,
        hoverColor: PALETTE.surfaceSoft,
        textColor: PALETTE.ink,
        fontSize: 15,
        borderColor: PALETTE.mist,
      });
      btn.onClick(() => this.selectDifficulty(d));
      this.addChild(btn);
      this.diffButtons.push(btn);
    }
    this.updateDifficultyVisuals();

    this.playButton = new Button({
      text: 'Start match',
      width: 232,
      height: 62,
      backgroundColor: PALETTE.accent,
      hoverColor: PALETTE.accentHover,
      fontSize: 22,
      borderColor: PALETTE.ink,
    });
    this.playButton.onClick(() => this.handlePlayClick());
    this.addChild(this.playButton);

    this.updateBackground();
    this.positionElements();
  }

  update(delta: number): void {
    this.elapsed += delta;
    this.positionElements();
  }

  exit(): void {
    logger.info('Exiting SplashScene');
  }

  unload(): void {
    this.removeChildren();
  }

  resize(width: number, height: number): void {
    super.resize(width, height);
    this.updateBackground();
    this.positionElements();
  }

  private updateBackground(): void {
    if (!this.background || !this.visualBackdrop || !this.horizon) return;

    const { width, height } = this.context;

    this.background.clear();
    this.background.beginFill(PALETTE.night);
    this.background.drawRect(0, 0, width, height);
    this.background.endFill();

    this.background.beginFill(PALETTE.forest, 0.4);
    this.background.drawCircle(width * 0.8, height * 0.3, Math.min(width, height) * 0.42);
    this.background.endFill();

    this.background.beginFill(PALETTE.accent, 0.08);
    this.background.drawCircle(width * 0.78, height * 0.34, Math.min(width, height) * 0.28);
    this.background.endFill();

    this.visualBackdrop.clear();
    this.visualBackdrop.beginFill(PALETTE.surface, 0.65);
    this.visualBackdrop.drawRoundedRect(width * 0.53, height * 0.16, width * 0.34, height * 0.62, 42);
    this.visualBackdrop.endFill();
    this.visualBackdrop.lineStyle(1.2, 0xffffff, 0.12);
    this.visualBackdrop.drawRoundedRect(width * 0.53, height * 0.16, width * 0.34, height * 0.62, 42);

    this.visualBackdrop.beginFill(PALETTE.accent, 0.08);
    this.visualBackdrop.drawCircle(width * 0.71, height * 0.38, 130);
    this.visualBackdrop.endFill();

    this.horizon.clear();
    this.horizon.beginFill(PALETTE.nightSoft, 1);
    this.horizon.drawPolygon([
      0,
      height,
      0,
      height * 0.74,
      width * 0.18,
      height * 0.69,
      width * 0.34,
      height * 0.72,
      width * 0.49,
      height * 0.68,
      width * 0.66,
      height * 0.76,
      width,
      height * 0.72,
      width,
      height,
    ]);
    this.horizon.endFill();
  }

  private positionElements(): void {
    const { width, height } = this.context;
    const left = Math.max(56, width * 0.09);
    const baseY = Math.max(82, height * 0.14);
    /** Extra space between the headline and the subtitle block */
    const titleBelowSpacing = 24;
    const heroCenterX = width * 0.71;
    const heroCenterY = height * 0.45;
    const introTime = (performance.now() - this.introStartedAt) / 1000;

    const eyebrowProgress = easeOutCubic(clamp((introTime - 0.05) / 0.65, 0, 1));
    const titleProgress = easeOutCubic(clamp((introTime - 0.18) / 0.8, 0, 1));
    const copyProgress = easeOutCubic(clamp((introTime - 0.42) / 0.8, 0, 1));
    const ctaProgress = easeOutCubic(clamp((introTime - 0.62) / 0.75, 0, 1));

    const titleSize = width < 900 ? 74 : 92;
    const subtitleSize = width < 900 ? 20 : 24;
    const detailSize = width < 900 ? 14 : 16;

    if (this.title) {
      this.title.style.fontSize = titleSize;
      this.title.style.lineHeight = titleSize * 0.95;
      this.title.x = left;
      this.title.y = lerp(baseY + 34, baseY, titleProgress);
      this.title.alpha = titleProgress;
    }

    if (this.eyebrow) {
      this.eyebrow.x = left;
      this.eyebrow.y = lerp(baseY - 16, baseY - 24, eyebrowProgress);
      this.eyebrow.alpha = eyebrowProgress;
    }

    if (this.subtitle) {
      this.subtitle.style.fontSize = subtitleSize;
      this.subtitle.x = left;
      this.subtitle.y = lerp(baseY + 224 + titleBelowSpacing, baseY + 206 + titleBelowSpacing, copyProgress);
      this.subtitle.alpha = copyProgress;
    }

    if (this.detail) {
      this.detail.style.fontSize = detailSize;
      this.detail.x = left;
      this.detail.y = lerp(baseY + 318 + titleBelowSpacing, baseY + 294 + titleBelowSpacing, copyProgress);
      this.detail.alpha = copyProgress * 0.92;
    }

    const diffBaseY = baseY + 360 + titleBelowSpacing;
    if (this.difficultyLabel) {
      this.difficultyLabel.x = left;
      this.difficultyLabel.y = lerp(diffBaseY + 18, diffBaseY, ctaProgress);
      this.difficultyLabel.alpha = ctaProgress * 0.7;
    }
    const difficulties: Difficulty[] = ['easy', 'medium', 'hard'];
    for (let i = 0; i < this.diffButtons.length; i++) {
      const selectionAlpha = difficulties[i] === this.selectedDifficulty ? 1.0 : 0.45;
      this.diffButtons[i].x = left + i * 96;
      this.diffButtons[i].y = lerp(diffBaseY + 36, diffBaseY + 20, ctaProgress);
      this.diffButtons[i].alpha = selectionAlpha * ctaProgress;
    }

    if (this.playButton) {
      this.playButton.x = left;
      this.playButton.y = lerp(baseY + 488 + titleBelowSpacing, baseY + 460 + titleBelowSpacing, ctaProgress);
      this.playButton.alpha = ctaProgress;
      this.playButton.scale.set(0.96 + ctaProgress * 0.07);
    }

    if (this.footer) {
      this.footer.x = left;
      this.footer.y = lerp(baseY + 580 + titleBelowSpacing, baseY + 544 + titleBelowSpacing, ctaProgress);
      this.footer.alpha = ctaProgress * 0.9;
    }

    if (this.kiwiDisplay) {
      this.kiwiDisplay.x = heroCenterX - 74 + Math.sin(this.elapsed * 0.7) * 8;
      this.kiwiDisplay.y = heroCenterY - 72 + Math.cos(this.elapsed * 1.1) * 10;
      this.kiwiDisplay.alpha = titleProgress;
      this.kiwiDisplay.scale.set(0.92 + titleProgress * 0.12);
    }

    if (this.coconutDisplay) {
      this.coconutDisplay.x = heroCenterX + 86 + Math.cos(this.elapsed * 0.6) * 10;
      this.coconutDisplay.y = heroCenterY + 58 + Math.sin(this.elapsed * 0.9) * 8;
      this.coconutDisplay.alpha = titleProgress;
      this.coconutDisplay.scale.set(0.9 + titleProgress * 0.14);
    }

    if (this.versusText) {
      this.versusText.x = heroCenterX + Math.sin(this.elapsed * 1.2) * 4;
      this.versusText.y = heroCenterY + 8;
      this.versusText.alpha = titleProgress;
    }
  }

  private selectDifficulty(difficulty: Difficulty): void {
    this.selectedDifficulty = difficulty;
    setDifficulty(difficulty);
    this.updateDifficultyVisuals();
  }

  private updateDifficultyVisuals(): void {
    const difficulties: Difficulty[] = ['easy', 'medium', 'hard'];
    for (let i = 0; i < this.diffButtons.length; i++) {
      this.diffButtons[i].alpha = difficulties[i] === this.selectedDifficulty ? 1.0 : 0.45;
    }
  }

  private handlePlayClick(): void {
    logger.info('Play button clicked');
    const app = this.context.app as unknown as {
      sceneManager?: { changeScene: (scene: typeof GameScene, fade: boolean) => void };
    };

    if (app.sceneManager) {
      app.sceneManager.changeScene(GameScene, true);
    }
  }
}
